import React, { useState, useEffect } from 'react';
import axios from 'axios';
import styles from './ViewCampaignModal.module.css';
import { AiOutlineClose, AiOutlineBarChart } from 'react-icons/ai';
import { URL as BASEURL } from '../../AxiosUrlGlobal';

const ViewCampaignModal = ({ isOpen, onClose, campaign }) => {
  const [analytics, setAnalytics] = useState(null);
  const [loadingAnalytics, setLoadingAnalytics] = useState(false);

  const userId = localStorage.getItem('id');
  const token = localStorage.getItem('token');

  // Fetch analytics when modal opens and campaign has tracking links
  useEffect(() => {
    if (isOpen && campaign && campaign.Status === 3) {
      fetchAnalytics();
    } else {
      setAnalytics(null);
    }
  }, [isOpen, campaign]);

  const fetchAnalytics = async () => {
    try {
      setLoadingAnalytics(true);
      const response = await axios.get(
        `${BASEURL}superadmin/sms-marketing/analytics/${userId}/${campaign._id}`,
        { headers: { token } }
      );

      if (response.data.success) {
        setAnalytics(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching analytics:', err);
    } finally {
      setLoadingAnalytics(false);
    }
  };

  if (!isOpen || !campaign) return null;

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusLabel = (status) => {
    const labels = {
      0: 'Draft',
      1: 'Scheduled',
      2: 'Sending',
      3: 'Sent',
      4: 'Failed',
      5: 'Cancelled'
    };
    return labels[status] || 'Unknown';
  };

  const getStatusColor = (status) => {
    const colors = {
      0: '#6b7280',
      1: '#3b82f6',
      2: '#f59e0b',
      3: '#10b981',
      4: '#ef4444',
      5: '#6b7280'
    };
    return colors[status] || '#6b7280';
  };

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h2>Campaign Details</h2>
          <button className={styles.closeButton} onClick={onClose}>
            <AiOutlineClose />
          </button>
        </div>

        <div className={styles.modalBody}>
          {/* Campaign Info */}
          <div className={styles.section}>
            <div className={styles.field}>
              <label>Campaign Title</label>
              <div className={styles.value}>{campaign.Title}</div>
            </div>

            <div className={styles.field}>
              <label>Status</label>
              <div className={styles.value}>
                <span
                  className={styles.statusBadge}
                  style={{ backgroundColor: getStatusColor(campaign.Status) }}
                >
                  {getStatusLabel(campaign.Status)}
                </span>
              </div>
            </div>

            <div className={styles.field}>
              <label>Target Audience</label>
              <div className={styles.value}>
                <span className={styles.audienceBadge}>
                  {campaign.targetAudience || 'All Users'}
                </span>
              </div>
            </div>
          </div>

          {/* Message Body */}
          <div className={styles.section}>
            <div className={styles.field}>
              <label>Message Body</label>
              <div className={styles.messageBox}>
                {campaign.Body}
              </div>
              <small className={styles.helpText}>
                {campaign.Body.length} characters
              </small>
            </div>
          </div>

          {/* Custom Recipients */}
          {campaign.targetAudience === 'custom' && campaign.customRecipients?.length > 0 && (
            <div className={styles.section}>
              <div className={styles.field}>
                <label>Custom Recipients ({campaign.customRecipients.length})</label>
                <div className={styles.recipientsList}>
                  {campaign.customRecipients.map((recipient, index) => (
                    <div key={index} className={styles.recipientItem}>
                      <span>{recipient.name}</span>
                      <span className={styles.mobile}>{recipient.mobile}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Statistics */}
          <div className={styles.section}>
            <h3>Campaign Statistics</h3>
            <div className={styles.statsGrid}>
              <div className={styles.statItem}>
                <label>Total Recipients</label>
                <div className={styles.statValue}>{campaign.stats?.totalRecipients || 0}</div>
              </div>
              <div className={styles.statItem}>
                <label>Sent Count</label>
                <div className={styles.statValue}>{campaign.stats?.sentCount || 0}</div>
              </div>
              <div className={styles.statItem}>
                <label>Failed Count</label>
                <div className={styles.statValue}>{campaign.stats?.failedCount || 0}</div>
              </div>
              <div className={styles.statItem}>
                <label>Credits Used</label>
                <div className={styles.statValue}>{campaign.stats?.creditsUsed || 0}</div>
              </div>
            </div>
          </div>

          {/* Click Analytics (only show for sent campaigns with tracking) */}
          {campaign.Status === 3 && (
            <div className={styles.section}>
              <h3>
                <AiOutlineBarChart style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                Click Analytics
              </h3>

              {loadingAnalytics && (
                <div className={styles.loading}>Loading analytics...</div>
              )}

              {!loadingAnalytics && analytics && analytics.hasTrackingLinks && (
                <>
                  <div className={styles.statsGrid}>
                    <div className={styles.statItem}>
                      <label>Total Clicks</label>
                      <div className={styles.statValue}>{analytics.stats.totalClicks || 0}</div>
                    </div>
                    <div className={styles.statItem}>
                      <label>Unique Clickers</label>
                      <div className={styles.statValue}>{analytics.stats.uniqueClickers || 0}</div>
                    </div>
                    <div className={styles.statItem}>
                      <label>Click Rate</label>
                      <div className={styles.statValue}>{analytics.stats.clickRate || 0}%</div>
                    </div>
                    <div className={styles.statItem}>
                      <label>Not Clicked</label>
                      <div className={styles.statValue}>{analytics.engagement?.notClicked || 0}</div>
                    </div>
                  </div>

                  {/* Engagement Breakdown */}
                  {analytics.engagement && (
                    <div className={styles.engagementSection}>
                      <label>Engagement Breakdown</label>
                      <div className={styles.engagementGrid}>
                        <div className={styles.engagementItem}>
                          <span>Clicked Once</span>
                          <strong>{analytics.engagement.clickedOnce || 0}</strong>
                        </div>
                        <div className={styles.engagementItem}>
                          <span>Multiple Clicks</span>
                          <strong>{analytics.engagement.clickedMultiple || 0}</strong>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Top Clickers */}
                  {analytics.topClickers && analytics.topClickers.length > 0 && (
                    <div className={styles.topClickersSection}>
                      <label>Top Clickers</label>
                      <div className={styles.topClickersList}>
                        {analytics.topClickers.slice(0, 5).map((clicker, index) => (
                          <div key={index} className={styles.topClickerItem}>
                            <div className={styles.clickerInfo}>
                              <span className={styles.clickerName}>{clicker.recipientName}</span>
                              <span className={styles.clickerMobile}>{clicker.recipientMobile}</span>
                            </div>
                            <div className={styles.clickCount}>
                              {clicker.clickCount} {clicker.clickCount === 1 ? 'click' : 'clicks'}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}

              {!loadingAnalytics && analytics && !analytics.hasTrackingLinks && (
                <div className={styles.noAnalytics}>
                  This campaign does not contain any trackable links.
                </div>
              )}
            </div>
          )}

          {/* Dates */}
          <div className={styles.section}>
            <h3>Timeline</h3>
            <div className={styles.timeline}>
              <div className={styles.timelineItem}>
                <label>Created At</label>
                <div className={styles.timelineValue}>
                  {formatDate(campaign.Created_At)}
                </div>
              </div>

              {campaign.Schedule && (
                <div className={styles.timelineItem}>
                  <label>Scheduled For</label>
                  <div className={styles.timelineValue}>
                    {formatDate(campaign.Schedule)}
                  </div>
                </div>
              )}

              {campaign.sentAt && (
                <div className={styles.timelineItem}>
                  <label>Sent At</label>
                  <div className={styles.timelineValue}>
                    {formatDate(campaign.sentAt)}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className={styles.modalFooter}>
          <button onClick={onClose} className={styles.closeBtn}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ViewCampaignModal;
